import React, { useRef, useState } from 'react';
import './ActivityLegend.css';
import { parseKmzFile } from '../utils/kmzParser';

const ActivityLegend = ({ 
  activities,
  activityIds,
  activityColors, 
  visibleActivities, 
  onToggleActivity,
  onColorChange,
  kmzOverlays,
  onAddKmzOverlay,
  onRemoveKmzOverlay,
  onToggleKmzOverlay,
  onToggleKmzGroup,
  onKmzOpacityChange,
}) => {
  const fileInputRef = useRef(null);
  const [kmzLoading, setKmzLoading] = useState(false);
  const [kmzError, setKmzError] = useState(null);

  const handleKmzImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setKmzLoading(true);
    setKmzError(null);
    try {
      const result = await parseKmzFile(file);
      onAddKmzOverlay(result);
    } catch (err) {
      setKmzError(err.message);
    } finally {
      setKmzLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  // Safety checks
  if (!activities || !Array.isArray(activities) || activities.length === 0) {
    return (
      <div className="activity-legend">
        <h2>Activities</h2>
        <div className="empty-state">No activity data available.</div>
      </div>
    );
  }

  if (!activityIds || !Array.isArray(activityIds) || activityIds.length === 0) {
    return (
      <div className="activity-legend">
        <h2>Activities</h2>
        <div className="empty-state">No activities to display.</div>
      </div>
    );
  }

  // Group activities by ID with metadata
  const activityMetadata = activityIds.map(actId => {
    const activityPoints = activities.filter(a => a.activityId === actId);
    if (activityPoints.length === 0) return null;
    
    const firstPoint = activityPoints[0];
    const pointCount = activityPoints.length;
    
    // Calculate duration safely
    let duration = 0;
    try {
      const timestamps = activityPoints
        .map(p => p.timestamp)
        .filter(t => t != null)
        .map(t => new Date(t).getTime())
        .filter(t => !isNaN(t));
      
      if (timestamps.length > 1) {
        duration = Math.round((Math.max(...timestamps) - Math.min(...timestamps)) / 60000); // minutes
      }
    } catch (error) {
      console.error('Error calculating duration:', error);
      duration = 0;
    }
    
    return {
      activityId: actId,
      userId: firstPoint.userId || 'Unknown',
      createdAt: firstPoint.createdAt || new Date().toISOString(),
      pointCount,
      duration
    };
  }).filter(Boolean);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleToggleAll = () => {
    if (visibleActivities.size === activityIds.length) {
      // Hide all
      activityIds.forEach(id => onToggleActivity(id, false));
    } else {
      // Show all
      activityIds.forEach(id => onToggleActivity(id, true));
    }
  };

  return (
    <div className="activity-legend">
      <h2>Activities</h2>
      
      <div className="legend-controls">
        <button onClick={handleToggleAll} className="toggle-all-btn">
          {visibleActivities.size === activityIds.length ? 'Hide All' : 'Show All'}
        </button>
      </div>

      <div className="legend-items">
        {activityMetadata.map((activity) => {
          const isVisible = visibleActivities.has(activity.activityId);
          const activityColor = activityColors?.[activity.activityId] || '#fc5f21';
          
          return (
            <div 
              key={activity.activityId}
              className={`legend-item ${isVisible ? 'visible' : 'hidden'}`}
              onClick={() => onToggleActivity(activity.activityId)}
              style={{
                borderColor: isVisible ? activityColor : '#ddd',
                backgroundColor: isVisible ? `${activityColor}15` : 'white'
              }}
            >
              <div className="legend-checkbox">
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => onToggleActivity(activity.activityId)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              
              <label className="legend-color-swatch" onClick={(e) => e.stopPropagation()}>
                <span
                  className="color-preview"
                  style={{ backgroundColor: activityColor }}
                />
                <input
                  type="color"
                  value={activityColor}
                  onChange={(e) => onColorChange(activity.activityId, e.target.value)}
                  className="color-input-hidden"
                />
              </label>
              
              <div className="legend-info">
                <div className="activity-id-short">
                  {activity.activityId}
                </div>
                <div className="activity-meta">
                  <div className="meta-row">
                    <span className="meta-label">User:</span>
                    <span className="meta-value">{activity.userId}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Created:</span>
                    <span className="meta-value">{formatDate(activity.createdAt)}</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Duration:</span>
                    <span className="meta-value">{activity.duration}m</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Points:</span>
                    <span className="meta-value">{activity.pointCount}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* KMZ / KML Trail Overlay */}
      <div className="kmz-overlay-section">
        <h2>Trail Overlays</h2>
        <input
          ref={fileInputRef}
          type="file"
          accept=".kmz,.kml"
          onChange={handleKmzImport}
          style={{ display: 'none' }}
        />
        <button
          className="toggle-all-btn kmz-import-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={kmzLoading}
        >
          {kmzLoading ? 'Importing...' : '+ Import KMZ / KML'}
        </button>
        {kmzError && <div className="kmz-error">{kmzError}</div>}

        {kmzOverlays && kmzOverlays.length > 0 && (
          <div className="kmz-overlay-list">
            {kmzOverlays.map(overlay => (
              <div key={overlay.id} className="kmz-overlay-card">
                {/* Overlay header */}
                <div className={`kmz-overlay-header ${overlay.visible ? '' : 'hidden'}`}>
                  <div className="legend-checkbox">
                    <input
                      type="checkbox"
                      checked={overlay.visible}
                      onChange={() => onToggleKmzOverlay(overlay.id)}
                    />
                  </div>
                  <div className="kmz-overlay-info">
                    <div className="kmz-overlay-name" title={overlay.filename}>{overlay.filename}</div>
                    <div className="kmz-overlay-meta">
                      {overlay.groups.length} group{overlay.groups.length !== 1 ? 's' : ''} &middot; {overlay.featureCount} feature{overlay.featureCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <button
                    className="kmz-remove-btn"
                    onClick={() => onRemoveKmzOverlay(overlay.id)}
                    title="Remove overlay"
                  >
                    ×
                  </button>
                </div>

                {/* Opacity slider */}
                {overlay.visible && (
                  <div className="kmz-opacity-row">
                    <span className="kmz-opacity-label">Opacity</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={overlay.opacity}
                      onChange={(e) => onKmzOpacityChange(overlay.id, parseFloat(e.target.value))}
                      className="kmz-opacity-slider"
                    />
                    <span className="kmz-opacity-value">{Math.round(overlay.opacity * 100)}%</span>
                  </div>
                )}

                {/* Per-group toggles */}
                {overlay.visible && overlay.groups.length > 1 && (
                  <div className="kmz-group-list">
                    {overlay.groups.map(group => (
                      <div
                        key={group.id}
                        className={`kmz-group-row ${group.visible ? '' : 'hidden'}`}
                        onClick={() => onToggleKmzGroup(overlay.id, group.id)}
                      >
                        <input
                          type="checkbox"
                          checked={group.visible}
                          onChange={() => onToggleKmzGroup(overlay.id, group.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          className="kmz-group-color"
                          style={{ backgroundColor: group.color || '#8B5CF6' }}
                        />
                        <span className="kmz-group-name" title={group.path}>
                          {group.name}
                        </span>
                        <span className="kmz-group-count">{group.featureCount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLegend;
